import { registerPlugin, WebPlugin } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';


export interface CambioAparienciaSistemaInfo {
    darkOS: boolean;
}

export interface ViewportReajustadoInfo {
    timestamp: number;
    orientationChanged?: boolean;
    fase?: 'inmediata' | 'diferida' | 'post_fullscreen_inmediata' | 'post_fullscreen_diferida';
}

export interface AparienciaPlugin {
    cambiarColorYEstadoDeModoOscuro(options: { fondo: string; oscuroActivo: boolean | null; }): Promise<void>;

    obtenerEstadoDeModoOscuroDelSistema(): Promise<{ darkOS: boolean; }>;
    obtenerOrientacionActual(): Promise<{ orientacion: string }>;

    suscribirACambiosDeModoOscuroDelSistema(): Promise<void>;
    desuscribirACambiosDeModoOscuroDelSistema(): Promise<void>;

    actualizarOrientacionesPermitidas(options: { orientacion: string; }): Promise<void>;
    forzarReevaluacionOrientaciones(): Promise<void>;

    addListener(eventName: 'nuevoEstadoDelModoOscuroDelSistema', listenerFunc: (info: CambioAparienciaSistemaInfo) => void): Promise<PluginListenerHandle>;
    addListener(eventName: 'viewportReajustado', listenerFunc: (info: ViewportReajustadoInfo) => void): Promise<PluginListenerHandle>;
    addListener(eventName: 'orientacionCambiada', listenerFunc: (info: { orientacion: string }) => void): Promise<PluginListenerHandle>;

    removeAllListeners(): Promise<void>;
}


class AparienciaWeb extends WebPlugin implements AparienciaPlugin {

  private matchMediaListener: ((event: MediaQueryListEvent) => void) | null = null;
  private mediaQueryList: MediaQueryList | null = null;
  private readonly EVENT_NOMBRE_CAMBIO_APARIENCIA = 'nuevoEstadoDelModoOscuroDelSistema';
  private sistemaChangeListener: ((event: MediaQueryListEvent) => void) | null = null;


  constructor() {
    super();
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    }
  }

  async obtenerOrientacionActual(): Promise<{ orientacion: string }> {
    return { orientacion: 'portrait' };
  }

  async cambiarColorYEstadoDeModoOscuro(options: { fondo: string, oscuroActivo: boolean | null }): Promise<void> {
    const fx = 'AparienciaWeb.cambiarColorYEstadoDeModoOscuro'

    try {
      // En web, el manejo de meta tags se delega al tema a través de useHead
      // El plugin solo se encarga de la lógica nativa en Android/iOS
      // consolo.log(fx, 'Plugin web delegando meta tags al tema (useHead):', {
      //     fondo: options.fondo,
      //     oscuroActivo: options.oscuroActivo
      // })

      return Promise.resolve()
    } catch (error) {
      consolo.error(`${fx} - Error:`, error)
      return Promise.resolve()
    }
  }

  async obtenerEstadoDeModoOscuroDelSistema(): Promise<{ darkOS: boolean }> {
    const fx = 'obtenerApariencia';
    try {
      if (typeof window === 'undefined') {
        // If using ErrorAumentado
        // throw new ErrorAumentado('No se puede identificar el tema por defecto en un entorno sin ventana (ej. SSR)');
        throw new Error('No se puede identificar el tema por defecto en un entorno sin ventana (ej. SSR)'); // Alternative without ErrorAumentado
      }

      if (!this.mediaQueryList) {
        // If using ErrorAumentado
        // throw new ErrorAumentado('window.matchMedia no soportado en este navegador para detectar el tema.');
        throw new Error('window.matchMedia no soportado en este navegador para detectar el tema.'); // Alternative without ErrorAumentado
      }

      const darkOS = this.mediaQueryList.matches;
      consolo.log('[apariencia]', 'oscuroActivo?', darkOS);

      return { darkOS };
    }
    catch (e: unknown) {
      consolo.error(`Error en ${fx}:`, e);
      throw e;
    }
  }

  async suscribirACambiosDeModoOscuroDelSistema(): Promise<void> {
    consolo.debug('[apariencia]', 'AparienciaWeb: suscribirACambiosDeModoOscuroDelSistema llamado');

    if (!this.mediaQueryList) {
      consolo.warn('window.matchMedia no soportado, no se puede suscribir a cambios.');
      return Promise.resolve();
    }

    // Listener para notificar a través del plugin
    if (this.matchMediaListener === null) {
      this.matchMediaListener = (event: MediaQueryListEvent) => {
        consolo.debug('MatchMedia change -> Notificando listeners del plugin:', event.matches);
        this.notifyListeners(this.EVENT_NOMBRE_CAMBIO_APARIENCIA, { darkOS: event.matches });
      };
      this.mediaQueryList.addEventListener('change', this.matchMediaListener);
      consolo.log('[apariencia]', '🎨 Tema: Listener de sistema añadido');
    } else {
      consolo.debug('[apariencia]', 'Listener de window.matchMedia ya estaba añadido.');
    }

    // Listener directo para cambios del sistema (notificación estándar)
    if (this.sistemaChangeListener === null) {
      this.sistemaChangeListener = (event: MediaQueryListEvent) => {
        consolo.log('[apariencia]', '🎨 Cambio de tema del sistema detectado:', event.matches ? 'oscuro' : 'claro');
        // Notificar usando el formato estándar del plugin
        this.notifyListeners('nuevoEstadoDelModoOscuroDelSistema', { darkOS: event.matches });
      };
      this.mediaQueryList.addEventListener('change', this.sistemaChangeListener);
      consolo.debug('[apariencia]', 'Listener directo del sistema añadido.');
    } else {
      consolo.debug('[apariencia]', 'Listener directo del sistema ya estaba añadido.');
    }

    return Promise.resolve();
  }

  async desuscribirACambiosDeModoOscuroDelSistema(): Promise<void> {
    consolo.debug('[apariencia]', 'AparienciaWeb: desuscribirACambiosDeModoOscuroDelSistema llamado');
    if (this.mediaQueryList && this.matchMediaListener !== null) {
      this.mediaQueryList.removeEventListener('change', this.matchMediaListener);
      this.matchMediaListener = null;
      consolo.debug('[apariencia]', 'Listener de window.matchMedia removido.');
    }

    if (this.mediaQueryList && this.sistemaChangeListener !== null) {
      this.mediaQueryList.removeEventListener('change', this.sistemaChangeListener);
      this.sistemaChangeListener = null;
      consolo.debug('[apariencia]', 'Listener directo del sistema removido.');
    }

    return Promise.resolve();
  }

  async actualizarOrientacionesPermitidas(options: { orientacion: string; }): Promise<void> {
    consolo.debug('[apariencia]', 'AparienciaWeb: actualizarOrientacionesPermitidas llamado - Web no hace nada', options);
    return Promise.resolve();
  }

  async forzarReevaluacionOrientaciones(): Promise<void> {
    consolo.debug('[apariencia]', 'AparienciaWeb: forzarReevaluacionOrientaciones llamado - Web no hace nada');
    return Promise.resolve();
  }
}

const Apariencia = registerPlugin<AparienciaPlugin>('Apariencia', {
  web: () => new AparienciaWeb()
});

export { Apariencia };